// ==============================================
// Screenshots — MyPolaris Screenshot Library
// ==============================================
//
// Every screenshot taken on a Scout (silent DPAD chord) uploads itself here
// under the owner's account. Day-grouped grid, per-Scout filters, lightbox
// viewer with caption/favourite/download, soft delete with undo, and a
// storage meter against the plan quota. The Scout deliberately has no
// gallery — this page IS the gallery.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhotoIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, CheckIcon } from '@heroicons/react/24/solid';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import clsx from 'clsx';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { ModalShell } from '../../components/ui/ModalShell';
import type { Screenshot } from '../../types/device';

const PAGE_SIZE = 60;

function dayLabel(iso: string): string {
  const d = parseISO(iso);
  if (isToday(d)) return `Today · ${format(d, 'EEEE d MMMM')}`;
  if (isYesterday(d)) return `Yesterday · ${format(d, 'EEEE d MMMM')}`;
  return format(d, 'EEEE d MMMM yyyy');
}

function fmtBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** "com.vortexmarine.vortexnav" → "VortexNav" — friendly app chip labels. */
const APP_LABELS: Record<string, string> = {
  'com.vortexmarine.vortexnav': 'VortexNav',
  'com.vortexmarine.launcher': 'Launcher',
  'com.vortexmarine.vortextide': 'VortexTide',
  'com.vortexmarine.vortexanchor': 'Anchor',
  'com.vortexmarine.truehelm': 'TrueHelm',
};
function appLabel(pkg: string | null): string | null {
  if (!pkg) return null;
  if (APP_LABELS[pkg]) return APP_LABELS[pkg];
  const tail = pkg.split('.').pop() ?? pkg;
  return tail.charAt(0).toUpperCase() + tail.slice(1);
}

function fmtPosition(lat: number | null, lon: number | null): string | null {
  if (lat == null || lon == null) return null;
  const f = (v: number, pos: string, neg: string) => {
    const abs = Math.abs(v);
    const deg = Math.floor(abs);
    const min = (abs - deg) * 60;
    return `${deg}°${min.toFixed(2)}'${v >= 0 ? pos : neg}`;
  };
  return `${f(lat, 'N', 'S')} ${f(lon, 'E', 'W')}`;
}

export function ScreenshotsPage() {
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();

  // Filters
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const [favOnly, setFavOnly] = useState(false);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  // Accumulated grid items ("Load more" appends)
  const [items, setItems] = useState<Screenshot[]>([]);
  const [total, setTotal] = useState(0);

  // Lightbox + selection
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const { data: storage } = useQuery({
    queryKey: ['screenshot-storage'],
    queryFn: () => api.getScreenshotStorage(),
  });

  const { data: devicesData } = useQuery({
    queryKey: ['my-devices'],
    queryFn: () => api.getMyDevices(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['screenshots', { device: deviceFilter, fav: favOnly, q, page }],
    queryFn: () =>
      api.getScreenshots({
        device: deviceFilter ?? undefined,
        favourite: favOnly,
        q: q || undefined,
        page,
        limit: PAGE_SIZE,
      }),
  });

  useEffect(() => {
    if (!data) return;
    setTotal(data.total);
    setItems((prev) => {
      if (data.page === 1) return data.items;
      const seen = new Set(prev.map((s) => s.id));
      return [...prev, ...data.items.filter((s) => !seen.has(s.id))];
    });
  }, [data]);

  const resetToFirstPage = () => {
    setPage(1);
    setSelectedIds(new Set());
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['screenshots'] });
    queryClient.invalidateQueries({ queryKey: ['screenshot-storage'] });
  };

  // ---- Mutations ----

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { caption?: string | null; favourite?: boolean } }) =>
      api.updateScreenshot(id, patch),
    onSuccess: (updated) => {
      setItems((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteScreenshot(id),
    onSuccess: (_void, id) => {
      const victim = items.find((s) => s.id === id);
      setItems((prev) => prev.filter((s) => s.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      setOpenId(null);
      setConfirmDelete(false);
      addToast({
        type: 'success',
        title: 'Screenshot deleted',
        action: victim
          ? {
              label: 'Undo',
              onClick: async () => {
                await api.restoreScreenshot(id);
                refresh();
              },
            }
          : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['screenshot-storage'] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.batchDeleteScreenshots(ids),
    onSuccess: (deleted) => {
      addToast({ type: 'success', title: `${deleted} screenshot${deleted === 1 ? '' : 's'} deleted` });
      setSelecting(false);
      setSelectedIds(new Set());
      resetToFirstPage();
      refresh();
    },
  });

  // ---- Derived ----

  const devices = devicesData?.devices ?? [];
  const showDeviceChips = devices.length > 1;

  const groups = useMemo(() => {
    const byDay = new Map<string, Screenshot[]>();
    for (const s of items) {
      const key = dayLabel(s.captured_at);
      const list = byDay.get(key);
      if (list) list.push(s);
      else byDay.set(key, [s]);
    }
    return Array.from(byDay.entries());
  }, [items]);

  const open = openId ? items.find((s) => s.id === openId) ?? null : null;
  const openIndex = open ? items.findIndex((s) => s.id === open.id) : -1;

  const step = (delta: number) => {
    const next = items[openIndex + delta];
    if (next) {
      setOpenId(next.id);
      setConfirmDelete(false);
    }
  };

  // Arrow-key navigation while the lightbox is open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openIndex]);

  const toggleCard = (s: Screenshot) => {
    if (selecting) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(s.id)) next.delete(s.id);
        else next.add(s.id);
        return next;
      });
    } else {
      setOpenId(s.id);
      setConfirmDelete(false);
    }
  };

  const quotaBytes = (storage?.quota_mb ?? 2048) * 1024 * 1024;
  const usedPct = storage ? Math.min(100, Math.round((storage.bytes / quotaBytes) * 100)) : 0;
  const noLibraryYet = !isLoading && total === 0 && !q && !deviceFilter && !favOnly;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Screenshots</h1>
          <p className="text-slate-400 mt-1 text-sm">
            {total > 0
              ? `${total} screenshot${total === 1 ? '' : 's'}${devices.length > 1 ? ` across ${devices.length} Scouts` : ''} — captured on your Scout, backed up here automatically`
              : 'Captures from your Scout, backed up here automatically'}
          </p>
        </div>
        <div className="flex items-end gap-4">
          {storage && storage.count > 0 && (
            <div className="min-w-[200px]">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                <span>Storage</span>
                <span className="text-slate-300 font-medium">
                  {fmtBytes(storage.bytes)} of {fmtBytes(quotaBytes)}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                <div
                  className={clsx(
                    'h-full rounded-full',
                    usedPct >= 90 ? 'bg-amber-500' : 'bg-gradient-to-r from-cyan-600 to-cyan-400',
                  )}
                  style={{ width: `${Math.max(2, usedPct)}%` }}
                />
              </div>
            </div>
          )}
          {items.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setSelecting((v) => !v);
                setSelectedIds(new Set());
              }}
              className={clsx(
                'px-4 py-1.5 rounded-lg border text-sm transition-colors',
                selecting
                  ? 'border-cyan-500/50 text-cyan-400 bg-cyan-500/10'
                  : 'border-slate-700 text-slate-300 bg-slate-800 hover:border-slate-600',
              )}
            >
              {selecting ? 'Done' : 'Select'}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label="All"
          active={!deviceFilter && !favOnly}
          onClick={() => {
            setDeviceFilter(null);
            setFavOnly(false);
            resetToFirstPage();
          }}
        />
        {showDeviceChips &&
          devices.map((d) => (
            <FilterChip
              key={d.id}
              label={d.name || d.serial_number}
              active={deviceFilter === d.id}
              onClick={() => {
                setDeviceFilter((prev) => (prev === d.id ? null : d.id));
                resetToFirstPage();
              }}
            />
          ))}
        <FilterChip
          label="★ Favourites"
          active={favOnly}
          onClick={() => {
            setFavOnly((v) => !v);
            resetToFirstPage();
          }}
        />
        <div className="ml-auto flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-500" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search caption or app…"
            className="bg-transparent text-sm text-white placeholder-slate-500 outline-none w-44"
          />
        </div>
      </div>

      {/* Grid */}
      {isLoading && items.length === 0 ? (
        <div className="card text-center py-16 text-slate-400">Loading screenshots…</div>
      ) : noLibraryYet ? (
        <EmptyState enabled={storage?.enabled ?? false} />
      ) : items.length === 0 ? (
        <div className="card text-center py-16 text-slate-400">
          Nothing matches — clear the filter or search.
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([label, shots]) => (
            <div key={label}>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{label}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {shots.map((s) => (
                  <ScreenshotCard
                    key={s.id}
                    shot={s}
                    selecting={selecting}
                    selected={selectedIds.has(s.id)}
                    onOpen={() => toggleCard(s)}
                    onToggleFav={() =>
                      patchMutation.mutate({ id: s.id, patch: { favourite: !s.favourite } })
                    }
                  />
                ))}
              </div>
            </div>
          ))}
          {items.length < total && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={isLoading}
                className="px-5 py-2 rounded-lg border border-slate-700 bg-slate-800 text-sm text-slate-300 hover:border-slate-600 disabled:opacity-50"
              >
                {isLoading ? 'Loading…' : `Load more (${total - items.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {selecting && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 bg-slate-950 border border-slate-600 rounded-xl px-5 py-3 shadow-2xl">
          <span className="text-sm text-slate-300">
            <b className="text-white">{selectedIds.size}</b> selected
          </span>
          <button
            type="button"
            onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
            disabled={bulkDeleteMutation.isPending}
            className="px-4 py-1.5 rounded-lg border border-red-500/50 text-red-300 text-sm hover:bg-red-500/10 disabled:opacity-50"
          >
            {bulkDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
          <button
            type="button"
            onClick={() => {
              setSelecting(false);
              setSelectedIds(new Set());
            }}
            className="px-4 py-1.5 rounded-lg border border-slate-600 text-slate-300 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Lightbox */}
      <ModalShell
        open={open != null}
        onClose={() => setOpenId(null)}
        title={
          open
            ? `${appLabel(open.foreground_app) ?? 'Screenshot'} · ${format(parseISO(open.captured_at), 'HH:mm')}`
            : 'Screenshot'
        }
        size="2xl"
        tone="dark"
      >
        {open && (
          <div className="flex flex-col lg:flex-row gap-5">
            <div className="relative flex-1 min-w-0 flex items-center justify-center bg-slate-950 rounded-lg overflow-hidden">
              <img
                src={api.screenshotImageUrl(open.id)}
                alt={open.caption || `Screenshot from ${open.device_name ?? 'Scout'}`}
                crossOrigin="use-credentials"
                className="max-h-[60vh] w-auto max-w-full object-contain"
              />
              <button
                type="button"
                onClick={() => step(-1)}
                disabled={openIndex <= 0}
                aria-label="Previous screenshot"
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-slate-900/80 border border-slate-600 text-white disabled:opacity-30"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                disabled={openIndex >= items.length - 1}
                aria-label="Next screenshot"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-slate-900/80 border border-slate-600 text-white disabled:opacity-30"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="lg:w-64 flex-none space-y-3 text-sm">
              <MetaRow label="Captured">
                {format(parseISO(open.captured_at), "EEEE d MMMM yyyy 'at' HH:mm")}
              </MetaRow>
              <MetaRow label="Device">{open.device_name ?? '—'}</MetaRow>
              <MetaRow label="App on screen">{appLabel(open.foreground_app) ?? '—'}</MetaRow>
              <MetaRow label="File">
                <span className="font-mono text-xs">
                  {open.width && open.height ? `${open.width}×${open.height} · ` : ''}
                  {fmtBytes(open.bytes)}
                </span>
              </MetaRow>
              {fmtPosition(open.lat, open.lon) && (
                <MetaRow label="Position">
                  <span className="font-mono text-xs inline-flex items-center gap-1">
                    <MapPinIcon className="w-3.5 h-3.5 text-slate-500" />
                    {fmtPosition(open.lat, open.lon)}
                  </span>
                </MetaRow>
              )}

              <div className="pt-1">
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                  Caption
                </label>
                <input
                  key={open.id}
                  defaultValue={open.caption ?? ''}
                  placeholder="Add a caption…"
                  maxLength={500}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next !== (open.caption ?? '')) {
                      patchMutation.mutate({ id: open.id, patch: { caption: next || null } });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 outline-none focus:border-cyan-600"
                />
              </div>

              <div className="pt-2 space-y-2">
                <a
                  href={api.screenshotImageUrl(open.id, true)}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-medium"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Download PNG
                </a>
                <button
                  type="button"
                  onClick={() =>
                    patchMutation.mutate({ id: open.id, patch: { favourite: !open.favourite } })
                  }
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  {open.favourite ? (
                    <>
                      <StarIconSolid className="w-4 h-4 text-amber-400" /> Favourited
                    </>
                  ) : (
                    <>
                      <StarIcon className="w-4 h-4" /> Favourite
                    </>
                  )}
                </button>
                {confirmDelete ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(open.id)}
                      disabled={deleteMutation.isPending}
                      className="flex-1 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Deleting…' : 'Confirm delete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      className="px-3 py-2 rounded-lg border border-slate-600 text-slate-300 text-sm"
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                )}
                <p className="text-[11px] text-slate-500 text-center pt-1">
                  Deleting here never touches the copy on your Scout.
                </p>
              </div>
            </div>
          </div>
        )}
      </ModalShell>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-3.5 py-1.5 rounded-full border text-sm transition-colors',
        active
          ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400 font-medium'
          : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-600',
      )}
    >
      {label}
    </button>
  );
}

function ScreenshotCard({
  shot,
  selecting,
  selected,
  onOpen,
  onToggleFav,
}: {
  shot: Screenshot;
  selecting: boolean;
  selected: boolean;
  onOpen: () => void;
  onToggleFav: () => void;
}) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const src = shot.has_thumb && !thumbFailed
    ? api.screenshotThumbUrl(shot.id)
    : api.screenshotImageUrl(shot.id);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={clsx(
        'group relative aspect-video rounded-xl overflow-hidden border bg-slate-800 cursor-pointer',
        'transition-all hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400',
        selected ? 'border-cyan-400' : 'border-slate-700 hover:border-slate-600',
      )}
      aria-label={`Screenshot from ${shot.device_name ?? 'Scout'} at ${format(parseISO(shot.captured_at), 'HH:mm')}`}
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        crossOrigin="use-credentials"
        onError={() => setThumbFailed(true)}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Bottom meta strip */}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 px-2.5 pb-1.5 pt-6 bg-gradient-to-t from-slate-950/90 to-transparent">
        {appLabel(shot.foreground_app) && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/15 text-white">
            {appLabel(shot.foreground_app)}
          </span>
        )}
        <span className="ml-auto text-[11px] font-mono text-slate-200">
          {format(parseISO(shot.captured_at), 'HH:mm')}
        </span>
      </div>

      {/* Selection tick */}
      {selecting && (
        <span
          className={clsx(
            'absolute top-2 left-2 w-6 h-6 rounded-md border-2 grid place-items-center',
            selected ? 'bg-cyan-400 border-cyan-400 text-slate-900' : 'border-white/70 bg-slate-950/40',
          )}
        >
          {selected && <CheckIcon className="w-4 h-4" />}
        </span>
      )}

      {/* Favourite */}
      {!selecting && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFav();
          }}
          aria-label={shot.favourite ? 'Remove favourite' : 'Favourite'}
          className={clsx(
            'absolute top-2 right-2 p-1.5 rounded-lg bg-slate-950/60 transition-opacity',
            shot.favourite ? 'opacity-100 text-amber-400' : 'opacity-0 group-hover:opacity-100 text-slate-300',
          )}
        >
          {shot.favourite ? <StarIconSolid className="w-4 h-4" /> : <StarIcon className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-slate-700/70 pt-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className="text-slate-300">{children}</div>
    </div>
  );
}

function EmptyState({ enabled }: { enabled: boolean }) {
  return (
    <div className="border border-dashed border-slate-600 rounded-xl bg-slate-800/40 text-center py-14 px-6">
      <div className="flex justify-center gap-3 mb-5">
        <Keycap symbol="▼" hint="hold" />
        <Keycap symbol="▲" hint="press" />
      </div>
      <h3 className="text-white font-semibold mb-1.5">No screenshots yet</h3>
      <p className="text-slate-400 text-sm max-w-md mx-auto">
        On your Scout, hold the down key and press up. The screenshot saves on the device and lands
        here automatically — nothing else to do.
      </p>
      {!enabled && (
        <p className="text-slate-500 text-xs mt-4 max-w-md mx-auto">
          Screenshot backup isn't included in your current plan. Captures still save on the Scout
          itself.
        </p>
      )}
      <div className="mt-5">
        <PhotoIcon className="w-6 h-6 text-slate-600 mx-auto" />
      </div>
    </div>
  );
}

function Keycap({ symbol, hint }: { symbol: string; hint: string }) {
  return (
    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-600 border-b-4 bg-slate-800 text-sm text-slate-300 font-mono">
      <span className="text-cyan-400">{symbol}</span> {hint}
    </span>
  );
}

export default ScreenshotsPage;
